import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { ContractResponse, DeltaResponse } from '../../api';
import { AsyncSection, PageHeader, Panel, type AsyncState } from '../../components';
import { formatDate, formatInteger, todayInAppTimeZone } from '../../format';
import { PATHS } from '../../routes';
import {
  useApprovedFutureDeltas,
  useMovementStatus,
  useNotifications as useNoticeFeed,
  usePendingDeltas,
  usePortalContracts,
  useRoster,
} from '../queries';
import { useCompanyScope } from './companyScope';
import { ACTION_LABELS, PORTAL_COPY, approvedAhead, contractLabel, currentCycle } from './portal';

/**
 * Where a customer's HR person lands, and the first question of their day.
 *
 * Every number here already has a screen of its own. This page exists because
 * the question somebody actually arrives with -- "is there anything I have to
 * do about the plan today?" -- is answered by four numbers that live on three
 * different screens, and opening all three to find out that the answer is "no"
 * is the tax this page removes.
 *
 * So it is a summary and stays one. Counts, one line of detail at most, and a
 * link to the screen that holds the rows -- never a fourth copy of the roster
 * or of the movement table. The moment a panel here grows a table, it has to be
 * kept in step with the real screen, and the two start disagreeing.
 *
 * **Nothing is derived that the server can be asked.** The cutoff date and its
 * sign-off come off the movement-status response rather than being worked out
 * from the contract's `cutoff_day`, for the reason `MovementStatusPage`
 * documents at length: a portal that decides for itself which month a change
 * lands in is a portal that eventually tells a customer something the
 * back-office disagrees with.
 *
 * **The company comes from the scope, never from the session.** A principal
 * entitled to more than one company switches in the header and every panel
 * below re-reads inside the company they chose.
 */

/** A number with its label, as the summary tiles render one. */
function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="summary__item">
      <dt>{label}</dt>
      <dd>{formatInteger(value)}</dd>
    </div>
  );
}

/**
 * One change in a few words -- the action and the date, off the same delta.
 *
 * Both halves come from one record for the reason `primaryScheduledChange`
 * gives: a label from one change beside a date from another is the sentence
 * that sends somebody to check a termination that is not the one they read
 * about.
 */
function changeSummary(delta: DeltaResponse): string {
  return `${ACTION_LABELS[delta.action]} em ${formatDate(delta.effective_date)}`;
}

/**
 * The panel titles, with the contract named only when there is a choice to be
 * made. See {@link contractLabel}.
 */
function titleFor(base: string, label: string | null): string {
  return label === null ? base : `${base} — ${label}`;
}

/**
 * One contract's three panels.
 *
 * A component rather than a loop body because each contract needs its own four
 * queries, and a hook cannot be called in a loop. It also means a company with
 * two contracts gets two independent sets of loading and error states instead
 * of one panel that waits for the slowest.
 */
function ContractSummary({
  companyId,
  contract,
  asOf,
  label,
}: {
  companyId: string;
  contract: ContractResponse;
  asOf: string;
  /** The contract's name, or `null` when the company has only this one. */
  label: string | null;
}) {
  const roster = useRoster(companyId, contract.id, asOf);
  const pending = usePendingDeltas(companyId, contract.id);
  const approved = useApprovedFutureDeltas(companyId, contract.id, asOf);
  // No cycle month is passed: which cutoff is being worked on is the server's
  // answer, in São Paulo. A laptop set to the wrong month must not be able to
  // change which month this page announces.
  const status = useMovementStatus(companyId, contract.id);

  const ahead = useMemo(
    () => approvedAhead(approved.data?.items ?? [], asOf),
    [approved.data, asOf],
  );

  /**
   * The two change reads, as one state.
   *
   * They answer one question between them -- what has been asked for and not
   * finished -- so reporting only the first would leave the panel looking
   * complete with half of it missing. Same shape as the roster screen's
   * `scheduledState`, and for the same reason.
   */
  const changesFailed = pending.isError || approved.isError;
  const changesState: AsyncState<{ pending: number; ahead: readonly DeltaResponse[] }> = {
    isPending: pending.isPending || approved.isPending,
    isError: changesFailed,
    error: pending.error ?? approved.error,
    data: changesFailed ? undefined : { pending: pending.data?.count ?? 0, ahead },
    refetch: () => {
      void pending.refetch();
      void approved.refetch();
    },
  };

  return (
    <>
      <Panel
        title={titleFor(PORTAL_COPY.overviewCoveredTitle, label)}
        description={PORTAL_COPY.overviewCoveredDescription}
      >
        <AsyncSection
          state={roster}
          loadingLabel="Carregando as pessoas cobertas…"
          errorTitle="Não foi possível carregar as pessoas cobertas"
          isEmpty={(data) => data.count === 0}
          emptyTitle={PORTAL_COPY.overviewCoveredEmpty}
          emptyDescription={PORTAL_COPY.overviewCoveredEmptyHint}
        >
          {(data) => (
            <>
              <dl className="summary">
                <Metric label={PORTAL_COPY.overviewCoveredMetric} value={data.count} />
              </dl>
              <p className="panel__footnote">Posição em {formatDate(asOf)}.</p>
            </>
          )}
        </AsyncSection>
        <p className="panel__actions panel__actions--footer">
          <Link to={PATHS.customerBeneficiaries}>{PORTAL_COPY.overviewCoveredLink}</Link>
        </p>
      </Panel>

      <Panel
        title={titleFor(PORTAL_COPY.overviewChangesTitle, label)}
        description={PORTAL_COPY.overviewChangesDescription}
      >
        <AsyncSection
          state={changesState}
          loadingLabel="Carregando as movimentações…"
          errorTitle="Não foi possível carregar as movimentações"
        >
          {(data) => (
            <>
              <dl className="summary">
                <Metric label={PORTAL_COPY.overviewPendingMetric} value={data.pending} />
                <Metric label={PORTAL_COPY.overviewAheadMetric} value={data.ahead.length} />
              </dl>
              {data.pending === 0 && data.ahead.length === 0 ? (
                <p className="panel__footnote">{PORTAL_COPY.overviewNoChanges}</p>
              ) : (
                data.ahead[0] !== undefined && (
                  // One line, not a table: the soonest thing coming is what
                  // makes the count mean something, and the rest are on the
                  // screen this links to.
                  <p className="panel__footnote">
                    {PORTAL_COPY.overviewNextChange} {changeSummary(data.ahead[0])}.
                  </p>
                )
              )}
            </>
          )}
        </AsyncSection>
        <p className="panel__actions panel__actions--footer">
          <Link to={PATHS.customerMovements}>{PORTAL_COPY.overviewChangesLink}</Link>
          <Link to={PATHS.customerStatus}>{PORTAL_COPY.overviewChangesStatusLink}</Link>
        </p>
      </Panel>

      <Panel
        title={titleFor(PORTAL_COPY.overviewCycleTitle, label)}
        description={PORTAL_COPY.overviewCycleDescription}
      >
        <AsyncSection
          state={status}
          loadingLabel="Carregando o fechamento do mês…"
          errorTitle="Não foi possível carregar o fechamento do mês"
        >
          {(data) => {
            const cycle = currentCycle(data);
            return (
              <dl className="definitions">
                <div>
                  <dt>{PORTAL_COPY.overviewCycleMonth}</dt>
                  <dd>{cycle.month}</dd>
                </div>
                <div>
                  <dt>{PORTAL_COPY.overviewCycleCutoff}</dt>
                  <dd>
                    {cycle.cutoffDate === null ? (
                      <span className="table__muted">{PORTAL_COPY.overviewCycleUnknownCutoff}</span>
                    ) : (
                      formatDate(cycle.cutoffDate)
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{PORTAL_COPY.overviewCycleSignOff}</dt>
                  <dd>
                    {cycle.isSignedOff === null ? (
                      <span className="table__muted">—</span>
                    ) : cycle.isSignedOff ? (
                      PORTAL_COPY.overviewCycleSignedOff
                    ) : (
                      PORTAL_COPY.overviewCycleOpen
                    )}
                  </dd>
                </div>
              </dl>
            );
          }}
        </AsyncSection>
        <p className="panel__actions panel__actions--footer">
          <Link to={PATHS.customerStatus}>{PORTAL_COPY.overviewCycleLink}</Link>
        </p>
      </Panel>
    </>
  );
}

export function CustomerOverviewPage() {
  const { company } = useCompanyScope();
  const contracts = usePortalContracts(company.id);
  const notices = useNoticeFeed(company.id);
  // Fixed for the life of the page, like the roster screen's: a date that moved
  // under a mounted page would silently change what "hoje" counted.
  const [asOf] = useState(todayInAppTimeZone);

  const items = contracts.data?.items ?? [];

  return (
    <div className="page">
      <PageHeader
        title="Visão geral"
        description={`Você está vendo os dados de ${company.name}.`}
      />

      <Panel title="O que você pode fazer aqui">
        <ul className="shortcuts">
          <li>
            <Link to={PATHS.customerBeneficiaries}>Ver quem está coberto hoje</Link> — com busca por
            nome e CPF, e o que já está agendado para os próximos meses.
          </li>
          <li>
            <Link to={PATHS.customerMovements}>Pedir uma movimentação</Link> — uma inclusão, uma
            exclusão ou uma alteração salarial, e acompanhar o que já foi pedido.
          </li>
          <li>
            <Link to={PATHS.customerStatus}>Acompanhar o que já foi pedido</Link> — onde está cada
            pedido: recebido, em análise, agendado para um mês à frente ou já no arquivo enviado.
          </li>
          <li>
            <Link to={PATHS.customerSpreadsheetUpload}>Enviar a planilha do mês</Link> — a mesma
            coisa em lote, quando o mês inteiro vem de uma vez.
          </li>
        </ul>
      </Panel>

      <AsyncSection
        state={contracts}
        loadingLabel="Carregando contrato…"
        errorTitle="Não foi possível carregar o contrato"
        isEmpty={(data) => data.items.length === 0}
        emptyTitle="Nenhum contrato"
        emptyDescription={PORTAL_COPY.noContract}
      >
        {() =>
          items.map((contract) => (
            <ContractSummary
              key={contract.id}
              companyId={company.id}
              contract={contract}
              asOf={asOf}
              label={items.length > 1 ? contractLabel(contract) : null}
            />
          ))
        }
      </AsyncSection>

      <Panel
        title={PORTAL_COPY.overviewNoticesTitle}
        description={PORTAL_COPY.overviewNoticesDescription}
      >
        <AsyncSection
          state={notices}
          loadingLabel="Carregando avisos…"
          errorTitle="Não foi possível carregar os avisos"
        >
          {(data) => (
            <>
              <dl className="summary">
                <Metric label={PORTAL_COPY.overviewUnreadMetric} value={data.unread_count} />
              </dl>
              {data.unread_count === 0 && (
                <p className="panel__footnote">{PORTAL_COPY.overviewAllRead}</p>
              )}
            </>
          )}
        </AsyncSection>
        <p className="panel__actions panel__actions--footer">
          <Link to={PATHS.customerStatus}>{PORTAL_COPY.overviewNoticesLink}</Link>
        </p>
      </Panel>
    </div>
  );
}
